import { useEffect } from "react";
import {
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Sparkles,
  FileText,
  Info,
  Clock,
  ShieldCheck,
} from "lucide-react";
import type { EvidenceValidationResult } from "../../api/frameworksApi";

interface ValidationDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error: string | null;
  data: EvidenceValidationResult | null;
  meta: {
    controlId: string;
    controlTitle?: string;
    evidenceName?: string;
  };
}

const formatDate = (iso: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusPalette = (status?: string) => {
  const s = (status ?? "").toUpperCase();
  if (s === "SATISFIED" || s === "PASS" || s === "PASSED") {
    return {
      label: "Satisfied",
      icon: CheckCircle2,
      className:
        "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20",
    };
  }
  if (s === "NOT_SATISFIED" || s === "FAIL" || s === "FAILED") {
    return {
      label: "Not Satisfied",
      icon: XCircle,
      className:
        "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20",
    };
  }
  if (s === "PARTIAL" || s === "PARTIALLY_SATISFIED") {
    return {
      label: "Partially Satisfied",
      icon: AlertTriangle,
      className:
        "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20",
    };
  }
  return {
    label: status || "Unknown",
    icon: Info,
    className:
      "bg-slate-50 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-500/20",
  };
};

const confidenceStyle = (score: number) => {
  const pct = score > 1 ? score : score * 100;
  if (pct >= 80)
    return "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20";
  if (pct >= 50)
    return "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20";
  return "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20";
};

const formatConfidence = (score: number) => {
  const pct = score > 1 ? score : score * 100;
  return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
};

export function ValidationDetailsDrawer({
  open,
  onClose,
  loading,
  error,
  data,
  meta,
}: ValidationDetailsDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const status = statusPalette(data?.status);
  const StatusIcon = status.icon;

  return (
    <>
      <div
        className="fixed inset-0 z-[50] bg-black/30 backdrop-blur-[2px] transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Evidence Validation Details"
        className="fixed top-0 right-0 z-[51] h-full w-[720px] max-w-[96vw] bg-white dark:bg-[#131320] border-l border-[#e2e8f0] dark:border-white/[0.08] shadow-2xl flex flex-col animate-[slideInRight_0.25s_ease-out]"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="size-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-indigo-50 dark:bg-violet-500/10">
              <ShieldCheck className="size-4 text-indigo-500 dark:text-violet-300" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2 py-0.5 rounded">
                  {meta.controlId}
                </span>
                <span className="text-[11px] font-semibold text-[#64748b] dark:text-white/50 tracking-wide">
                  Evidence Validation
                </span>
              </div>
              <h2 className="text-[15px] font-semibold text-[#0f172a] dark:text-white mt-1 leading-snug break-words">
                {meta.controlTitle || "Validation Details"}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="size-4 text-[#64748b] dark:text-white/50" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16">
              <Loader2 className="size-8 text-indigo-500 dark:text-violet-400 animate-spin" />
              <p className="text-[13px] font-medium text-[#475569] dark:text-white/60">
                Validating evidence against control statement...
              </p>
              <p className="text-[12px] text-[#94a3b8] dark:text-white/40">
                This may take a few seconds.
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16 text-center">
              <div className="size-12 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="size-6 text-red-500 dark:text-red-400" />
              </div>
              <p className="text-[14px] font-semibold text-[#0f172a] dark:text-white">
                Could not run validation
              </p>
              <p className="text-[12px] text-[#64748b] dark:text-white/60 max-w-md break-words">
                {error}
              </p>
            </div>
          )}

          {!loading && !error && !data && (
            <div className="flex flex-col items-center justify-center h-full gap-3 py-16 text-center">
              <div className="size-12 rounded-full bg-slate-100 dark:bg-white/[0.04] flex items-center justify-center">
                <FileText className="size-6 text-slate-400 dark:text-white/40" />
              </div>
              <p className="text-[14px] font-semibold text-[#0f172a] dark:text-white">
                No validation data
              </p>
              <p className="text-[12px] text-[#64748b] dark:text-white/60 max-w-md">
                Validation results will appear here once the request completes.
              </p>
            </div>
          )}

          {!loading && !error && data && (
            <div className="space-y-5">
              {/* Status + Confidence */}
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold ${status.className}`}
                >
                  <StatusIcon className="size-3.5" />
                  {status.label}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold ${confidenceStyle(data.confidenceScore)}`}
                  title="AI confidence in the validation result"
                >
                  <Sparkles className="size-3.5" />
                  Confidence {formatConfidence(data.confidenceScore)}
                </span>
                {data.modelUsed && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium bg-indigo-50 dark:bg-violet-500/10 text-indigo-700 dark:text-violet-300 border border-indigo-200 dark:border-violet-500/20">
                    <ShieldCheck className="size-3.5" />
                    {data.modelUsed}
                  </span>
                )}
              </div>

              {/* Document */}
              <section className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320]">
                <header className="flex items-center gap-2 px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
                  <FileText className="size-4 text-indigo-500 dark:text-violet-300" />
                  <h3 className="text-[13px] font-semibold text-[#0f172a] dark:text-white">
                    Validated Evidence
                  </h3>
                </header>
                <dl className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <div className="min-w-0">
                    <dt className="text-[11px] font-medium text-[#94a3b8] dark:text-white/50">
                      Evidence
                    </dt>
                    <dd
                      className="text-[13px] font-semibold text-[#0f172a] dark:text-white break-words"
                      title={meta.evidenceName || data.evidenceId}
                    >
                      {meta.evidenceName || data.evidenceId || "—"}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-[11px] font-medium text-[#94a3b8] dark:text-white/50">
                      Control
                    </dt>
                    <dd className="text-[13px] font-semibold text-[#0f172a] dark:text-white break-words">
                      {data.controlId || meta.controlId}
                    </dd>
                  </div>
                  <div className="min-w-0 sm:col-span-2 flex items-center gap-2">
                    <Clock className="size-3.5 text-[#94a3b8] dark:text-white/50" />
                    <span className="text-[12px] text-[#475569] dark:text-white/60">
                      Validated {formatDate(data.validatedAt)}
                    </span>
                  </div>
                </dl>
              </section>

              {/* Reasoning */}
              {data.reasoning && (
                <section className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320]">
                  <header className="flex items-center gap-2 px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
                    <Info className="size-4 text-indigo-500 dark:text-violet-300" />
                    <h3 className="text-[13px] font-semibold text-[#0f172a] dark:text-white">
                      Validation Summary
                    </h3>
                  </header>
                  <div className="px-4 py-3">
                    <p className="text-[13px] leading-relaxed text-[#334155] dark:text-white/85 whitespace-pre-wrap break-words">
                      {data.reasoning}
                    </p>
                  </div>
                </section>
              )}

              {/* Missing Requirements */}
              {data.missingRequirements && data.missingRequirements.length > 0 && (
                <section className="rounded-xl border border-red-200/60 dark:border-red-500/20 bg-red-50/40 dark:bg-red-500/[0.04]">
                  <header className="flex items-center gap-2 px-4 py-3 border-b border-red-200/60 dark:border-red-500/20">
                    <AlertTriangle className="size-4 text-red-500 dark:text-red-400" />
                    <h3 className="text-[13px] font-semibold text-[#0f172a] dark:text-white">
                      Missing Requirements ({data.missingRequirements.length})
                    </h3>
                  </header>
                  <ul className="px-4 py-3 space-y-2">
                    {data.missingRequirements.map((req, i) => (
                      <li
                        key={`${req}-${i}`}
                        className="flex items-start gap-2 text-[13px] text-[#334155] dark:text-white/85 leading-relaxed"
                      >
                        <span className="mt-1.5 size-1.5 rounded-full bg-red-500 flex-shrink-0" />
                        <span className="break-words">{req}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Supporting Evidence Chunks */}
              {data.supportingEvidenceChunks &&
                data.supportingEvidenceChunks.length > 0 && (
                  <section className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320]">
                    <header className="flex items-center gap-2 px-4 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06]">
                      <FileText className="size-4 text-indigo-500 dark:text-violet-300" />
                      <h3 className="text-[13px] font-semibold text-[#0f172a] dark:text-white">
                        Supporting Evidence ({data.supportingEvidenceChunks.length})
                      </h3>
                    </header>
                    <ul className="px-4 py-3 space-y-2">
                      {data.supportingEvidenceChunks.map((chunk, i) => (
                        <li
                          key={`${chunk.chunkId || i}`}
                          className="rounded-lg border border-[#e2e8f0]/70 dark:border-white/[0.05] bg-[#f8fafc] dark:bg-white/[0.02] px-3 py-2.5"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-[#0f172a] dark:text-white break-words">
                                {chunk.sectionTitle || "Section"}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-[#64748b] dark:text-white/60">
                                {chunk.pageRange && (
                                  <span className="inline-flex items-center gap-1">
                                    <FileText className="size-3" />
                                    Pages {chunk.pageRange}
                                  </span>
                                )}
                                {chunk.chunkId && (
                                  <span className="font-mono text-[10px] text-[#94a3b8] dark:text-white/40 break-all">
                                    {chunk.chunkId}
                                  </span>
                                )}
                              </div>
                            </div>
                            {typeof chunk.score === "number" && (
                              <span
                                className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${confidenceStyle(chunk.score)}`}
                                title="Relevance score"
                              >
                                <Sparkles className="size-3" />
                                {formatConfidence(chunk.score)}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
